import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightIcon from '@mui/icons-material/Flight';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { API_BASE_URL } from '../config';

const DARK_BG      = '#1a1d2e';
const DARK_SURFACE = '#242740';
const DARK_BORDER  = 'rgba(255,255,255,0.10)';
const ACCENT       = '#6C63FF';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED   = 'rgba(240,240,245,0.55)';

// MUI field styling for the dark surface
const fieldSx = {
    '& .MuiInputBase-root': { color: TEXT_PRIMARY, backgroundColor: 'rgba(255,255,255,0.03)' },
    '& .MuiInputLabel-root': { color: TEXT_MUTED },
    '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DARK_BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
    '& .MuiSvgIcon-root': { color: TEXT_MUTED },
};

const DEFAULT_DURATION = { transportation: 240, lodging: 0, activity: 90 };

// Day number within a trip for a given YYYY-MM-DD date (1-based)
const computeDay = (trip, dateStr) => {
    if (!trip || !trip.start_date || !dateStr) return 1;
    const start = new Date(trip.start_date + 'T00:00:00');
    const d = new Date(dateStr + 'T00:00:00');
    const diff = Math.floor((d - start) / 86400000);
    return Math.max(1, diff + 1);
};

// Pick the trip whose date range contains the activity date, else the first trip
const bestTripFor = (trips, dateStr) => {
    if (!trips || trips.length === 0) return null;
    if (dateStr) {
        const match = trips.find(t => t.start_date && t.end_date && dateStr >= t.start_date && dateStr <= t.end_date);
        if (match) return match;
    }
    return trips[0];
};

const splitDateTime = (raw, fallbackDate, fallbackTime) => {
    if (raw && typeof raw === 'string' && raw.includes(' ')) {
        const [d, t] = raw.split(' ');
        return { date: d, time: (t || '').slice(0, 5) || fallbackTime };
    }
    return { date: fallbackDate || '', time: (fallbackTime || '').slice(0, 5) };
};

// Guess whether an email is about lodging, transport or a generic activity.
// The subject is the strongest signal, so it's weighted heavily; the body
// breaks ties. Used to pre-select the right type when there's no extracted item.
const detectType = (message) => {
    const subject = (message?.subject || '').toLowerCase();
    const body = (message?.body || '').toLowerCase();
    const lodgingRe = /hotel|hostel|resort|motel|airbnb|apartment|guest\s?house|\blodge\b|booking at|check[-\s]?in|check[-\s]?out|\bnights?\b|reservation|room/g;
    const transportRe = /\bflight\b|airline|airways|boarding|e-?ticket|\bpnr\b|departure|\btrain\b|railway|car rental|rental car|\bferry\b|cruise|\bdepart(s|ed|ure)?\b/g;
    // "From <city> to <city>" route phrasing strongly implies transport even
    // when no explicit transport keyword is present (common in flight subjects).
    const routeRe = /\bfrom\s+[a-zÀ-ɏ][a-zÀ-ɏ.'\s-]*\s+to\s+[a-zÀ-ɏ]/g;
    const score = (txt, weight) => ({
        lodging: (txt.match(lodgingRe) || []).length * weight,
        transport: ((txt.match(transportRe) || []).length + (txt.match(routeRe) || []).length) * weight,
    });
    const s = score(subject, 3);
    const b = score(body, 1);
    const lodging = s.lodging + b.lodging;
    const transport = s.transport + b.transport;
    if (transport > lodging && transport > 0) return 'transportation';
    if (lodging > 0) return 'lodging';
    return 'activity';
};

const EmailReviewModal = ({ open, message, trips, onClose, onResolved }) => {
    // Working copy of the items still needing placement
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [view, setView] = useState('form'); // 'form' | 'email'
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(null);

    const activeTrips = useMemo(
        () => (trips || []).filter(t => t.start_date && t.end_date),
        [trips]
    );

    // Seed the working list whenever a new message opens.
    // If the email had no machine-extracted items (older emails, or parses that
    // found nothing), start from one blank entry the user fills in by hand —
    // they can read the source via the "Email text" toggle and copy details.
    useEffect(() => {
        if (open && message) {
            const unplaced = message.meta?.unplaced || [];
            setItems(unplaced.length > 0 ? unplaced : [{ id: null, type: detectType(message) }]);
            setIdx(0);
            setView('form');
            setError(null);
        }
    }, [open, message]);

    const current = items[idx] || null;

    // Build the editable form from the current item
    useEffect(() => {
        if (!current) { setForm(null); return; }
        const rawType = (current.type || 'activity').toLowerCase();
        const type = ['transportation', 'lodging', 'activity'].includes(rawType) ? rawType : 'activity';
        const sd = current.structured_data || {};
        const startRaw = current.start_date_time || null;
        const { date, time } = splitDateTime(
            startRaw,
            current.start_date,
            (current.start_time || '00:00').slice(0, 5)
        );
        const trip = bestTripFor(activeTrips, date);
        setForm({
            type,
            subtype: current.subtype || (type === 'transportation' ? 'flight' : type === 'lodging' ? 'hotel' : ''),
            title: current.title || '',
            notes: current.notes || '',
            tripId: trip ? String(trip.trip_id) : '',
            startDate: date || (trip ? trip.start_date : ''),
            startTime: time || '00:00',
            duration: current.duration || DEFAULT_DURATION[type] || 90,
            location: current.location || '',
            reference_ticket_no: current.reference_ticket_no || '',
            booking_agency: current.booking_agency || '',
            // transportation extras
            carrier_name: sd.carrier_name || current.booking_agency || '',
            flight_number: sd.flight_number || '',
            origin_airport_code: sd.origin_airport_code || '',
            destination_airport_code: sd.destination_airport_code || '',
            origin_city: sd.origin_city || '',
            destination_city: sd.destination_city || current.destination || '',
            seat_number: sd.seat_number || '',
        });
    }, [current, activeTrips]);

    const selectedTrip = useMemo(
        () => activeTrips.find(t => String(t.trip_id) === String(form?.tripId)) || null,
        [activeTrips, form?.tripId]
    );

    const dayNumber = useMemo(
        () => (form ? computeDay(selectedTrip, form.startDate) : 1),
        [selectedTrip, form]
    );

    const setField = useCallback((key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleTypeChange = (_e, newType) => {
        if (!newType) return;
        setForm(prev => ({
            ...prev,
            type: newType,
            subtype: newType === 'transportation' ? (prev.subtype || 'flight')
                   : newType === 'lodging' ? 'hotel' : '',
            duration: prev.duration || DEFAULT_DURATION[newType] || 90,
        }));
    };

    const buildStructuredData = (f) => {
        const sd = {};
        if (f.startDate && f.startTime) sd.start_date_time = `${f.startDate} ${f.startTime}`;
        if (f.type === 'transportation') {
            if (f.carrier_name) sd.carrier_name = f.carrier_name;
            if (f.flight_number) sd.flight_number = f.flight_number;
            if (f.origin_airport_code) sd.origin_airport_code = f.origin_airport_code;
            if (f.destination_airport_code) sd.destination_airport_code = f.destination_airport_code;
            if (f.origin_city) sd.origin_city = f.origin_city;
            if (f.destination_city) sd.destination_city = f.destination_city;
            if (f.seat_number) sd.seat_number = f.seat_number;
        }
        if (f.type === 'lodging' && f.location) sd.location = f.location;
        return sd;
    };

    const advanceOrClose = (remaining) => {
        if (remaining.length === 0) {
            onClose();
            return;
        }
        setItems(remaining);
        setIdx(i => Math.min(i, remaining.length - 1));
    };

    const handleSave = async () => {
        if (!form) return;
        if (!form.tripId) { setError('Please choose a trip.'); return; }
        if (!form.title.trim()) { setError('A title is required.'); return; }
        if (!form.startDate) { setError('A date is required.'); return; }

        setSaving(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const startTime = `${(form.startTime || '00:00')}:00`;
            const payload = {
                trip_id: Number(form.tripId),
                day: dayNumber,
                title: form.title.trim(),
                notes: form.notes || '',
                start_time: startTime,
                duration: Number(form.duration) || DEFAULT_DURATION[form.type] || 90,
                location: form.location || null,
                type: form.type,
                subtype: form.subtype || null,
                reference_ticket_no: form.reference_ticket_no || null,
                booking_agency: form.booking_agency || form.carrier_name || null,
                is_ai_suggestion: 0,
                is_pinned: 0,
                structured_data: buildStructuredData(form),
            };

            const res = await fetch(`${API_BASE_URL}/activities.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const msg = await res.json().catch(() => ({}));
                throw new Error(msg.message || 'Failed to save activity.');
            }

            // Mark this item resolved on the source email message
            await fetch(`${API_BASE_URL}/resolve_email_review.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ message_id: message.id, item_id: current.id, index: idx }),
            }).catch(() => {});

            const remaining = items.filter((_, i) => i !== idx);
            if (onResolved) onResolved(message.id, remaining.length);
            advanceOrClose(remaining);
        } catch (e) {
            setError(e.message || 'Something went wrong.');
        } finally {
            setSaving(false);
        }
    };

    const handleSkip = () => {
        setError(null);
        if (idx < items.length - 1) {
            setIdx(idx + 1);
        } else {
            onClose();
        }
    };

    if (!open || !message) return null;

    const total = items.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            sx={{ zIndex: 1500 }}
            PaperProps={{ style: { backgroundColor: DARK_BG, color: TEXT_PRIMARY } }}
        >
            <DialogTitle
                style={{ backgroundColor: DARK_SURFACE, borderBottom: `1px solid ${DARK_BORDER}` }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5 }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap style={{ color: TEXT_PRIMARY }}>
                        Place email into your plan
                    </Typography>
                    <Typography variant="caption" display="block" noWrap style={{ color: TEXT_MUTED }}>
                        {message.subject || '(no subject)'}
                    </Typography>
                </Box>
                {total > 1 && (
                    <Chip
                        label={`Item ${idx + 1} of ${total}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(108,99,255,0.18)', color: ACCENT, border: `1px solid ${ACCENT}55`, mr: 1 }}
                    />
                )}
                <IconButton onClick={onClose} size="small" sx={{ color: TEXT_MUTED, '&:hover': { color: TEXT_PRIMARY } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* View toggle: edit the details, or read the source email to copy from */}
            <Box
                sx={{ px: 3, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${DARK_BORDER}` }}
                style={{ backgroundColor: DARK_SURFACE }}
            >
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    size="small"
                    onChange={(_e, v) => v && setView(v)}
                    sx={{
                        '& .MuiToggleButton-root': { color: TEXT_MUTED, borderColor: DARK_BORDER, textTransform: 'none', px: 1.5 },
                        '& .MuiToggleButton-root.Mui-selected': { color: '#fff', backgroundColor: ACCENT },
                        '& .MuiToggleButton-root.Mui-selected:hover': { backgroundColor: '#574fd6' },
                    }}
                >
                    <ToggleButton value="form"><EditNoteIcon sx={{ fontSize: 18, mr: 0.5 }} />Edit details</ToggleButton>
                    <ToggleButton value="email"><EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />Email text</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" style={{ color: TEXT_MUTED }}>
                    {view === 'email' ? 'Select & copy details, then switch back to fill the form.' : 'Fill in any missing details and save.'}
                </Typography>
            </Box>

            <DialogContent sx={{ p: 0 }} style={{ backgroundColor: DARK_BG }}>
                {view === 'email' ? (
                    <Box
                        sx={{ p: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.7, userSelect: 'text' }}
                        style={{ color: TEXT_PRIMARY }}
                    >
                        {message.body || '(no email body stored)'}
                    </Box>
                ) : !form ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography style={{ color: TEXT_MUTED }}>Nothing left to place.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {error && <Alert severity="error" sx={{ bgcolor: 'rgba(244,67,54,0.12)', color: '#f28b82' }}>{error}</Alert>}

                        <Box>
                            <Typography variant="caption" sx={{ mb: 0.75, display: 'block' }} style={{ color: TEXT_MUTED }}>
                                What is this?
                            </Typography>
                            <ToggleButtonGroup
                                value={form.type}
                                exclusive
                                size="small"
                                onChange={handleTypeChange}
                                sx={{
                                    '& .MuiToggleButton-root': { color: TEXT_MUTED, borderColor: DARK_BORDER, textTransform: 'none', px: 2 },
                                    '& .MuiToggleButton-root.Mui-selected': { color: '#fff', backgroundColor: ACCENT },
                                    '& .MuiToggleButton-root.Mui-selected:hover': { backgroundColor: '#574fd6' },
                                }}
                            >
                                <ToggleButton value="activity"><LocalActivityIcon sx={{ fontSize: 18, mr: 0.5 }} />Activity</ToggleButton>
                                <ToggleButton value="lodging"><HotelIcon sx={{ fontSize: 18, mr: 0.5 }} />Lodging</ToggleButton>
                                <ToggleButton value="transportation"><FlightIcon sx={{ fontSize: 18, mr: 0.5 }} />Flight / Transport</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <TextField label="Title" value={form.title} onChange={e => setField('title', e.target.value)} fullWidth size="small" sx={fieldSx} />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                select label="Trip" value={form.tripId}
                                onChange={e => setField('tripId', e.target.value)}
                                fullWidth size="small" sx={fieldSx}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: DARK_SURFACE, color: TEXT_PRIMARY } } } }}
                            >
                                {activeTrips.length === 0 && <MenuItem value="" disabled>No active trips</MenuItem>}
                                {activeTrips.map(t => (
                                    <MenuItem key={t.trip_id} value={String(t.trip_id)}>
                                        {t.name || `Trip ${t.trip_id}`} ({t.start_date} → {t.end_date})
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Day" value={dayNumber} disabled
                                size="small" sx={{ ...fieldSx, width: 90 }}
                                helperText="auto"
                                FormHelperTextProps={{ sx: { color: TEXT_MUTED } }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Date" type="date" value={form.startDate}
                                onChange={e => setField('startDate', e.target.value)}
                                fullWidth size="small" sx={fieldSx} InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Time" type="time" value={form.startTime}
                                onChange={e => setField('startTime', e.target.value)}
                                fullWidth size="small" sx={fieldSx} InputLabelProps={{ shrink: true }}
                            />
                            {form.type !== 'lodging' && (
                                <TextField
                                    label="Duration (min)" type="number" value={form.duration}
                                    onChange={e => setField('duration', e.target.value)}
                                    size="small" sx={{ ...fieldSx, width: 140 }}
                                />
                            )}
                        </Box>

                        <TextField
                            label={form.type === 'lodging' ? 'Address' : 'Location'}
                            value={form.location} onChange={e => setField('location', e.target.value)}
                            fullWidth size="small" sx={fieldSx}
                        />

                        {form.type === 'transportation' && (
                            <>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField label="Carrier" value={form.carrier_name} onChange={e => setField('carrier_name', e.target.value)} fullWidth size="small" sx={fieldSx} />
                                    <TextField label="Flight / number" value={form.flight_number} onChange={e => setField('flight_number', e.target.value)} fullWidth size="small" sx={fieldSx} />
                                    <TextField label="Seat" value={form.seat_number} onChange={e => setField('seat_number', e.target.value)} size="small" sx={{ ...fieldSx, width: 110 }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField label="From (city)" value={form.origin_city} onChange={e => setField('origin_city', e.target.value)} fullWidth size="small" sx={fieldSx} />
                                    <TextField label="From (code)" value={form.origin_airport_code} onChange={e => setField('origin_airport_code', e.target.value)} size="small" sx={{ ...fieldSx, width: 120 }} />
                                    <TextField label="To (city)" value={form.destination_city} onChange={e => setField('destination_city', e.target.value)} fullWidth size="small" sx={fieldSx} />
                                    <TextField label="To (code)" value={form.destination_airport_code} onChange={e => setField('destination_airport_code', e.target.value)} size="small" sx={{ ...fieldSx, width: 120 }} />
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Booking reference" value={form.reference_ticket_no} onChange={e => setField('reference_ticket_no', e.target.value)} fullWidth size="small" sx={fieldSx} />
                            <TextField label="Booking agency" value={form.booking_agency} onChange={e => setField('booking_agency', e.target.value)} fullWidth size="small" sx={fieldSx} />
                        </Box>

                        <TextField label="Notes" value={form.notes} onChange={e => setField('notes', e.target.value)} fullWidth multiline minRows={2} size="small" sx={fieldSx} />
                    </Box>
                )}
            </DialogContent>

            <DialogActions style={{ backgroundColor: DARK_SURFACE, borderTop: `1px solid ${DARK_BORDER}` }} sx={{ px: 3, py: 1.5, gap: 1 }}>
                {total > 1 && (
                    <Button onClick={handleSkip} disabled={saving} sx={{ color: TEXT_MUTED, textTransform: 'none', '&:hover': { color: TEXT_PRIMARY } }}>
                        Skip
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} disabled={saving} sx={{ color: TEXT_MUTED, textTransform: 'none', '&:hover': { color: TEXT_PRIMARY } }}>
                    Close
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || view === 'email' || !form}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                    sx={{ bgcolor: ACCENT, color: '#fff', '&:hover': { bgcolor: '#574fd6' }, fontWeight: 600, textTransform: 'none' }}
                >
                    {total > 1 ? 'Save & next' : 'Save to plan'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailReviewModal;
